import useSuccessToast from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import TableHeaderControls from "@components/Project/TableHeaderControls";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableWithPagination from "@components/Tables/TableWithPagination";
import ConfirmDeleteModal from "@components/UI/ConfirmDeleteModal";
import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime, formatDateForRequest, translateDepartment } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { getIndexDBData, STORES, deleteDataInIndexedDb } from "@utils/indexedDb";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { refreshPaginatedListAfterDelete } from "@utils/pagination";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { PermissionContext } from "@contexts/PermissionContext";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import { SCAN_TOOL_OPTIONS } from "@constants/scanTool";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { useDataSyncRefresh } from "@hooks/useDataSyncRefresh";

const VerificationReportList = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [verificationReportList, setVerificationReportList] = useState([]);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.VERIFICATION_REPORT_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [searchFilters, setSearchFilters] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showModalDelete, setShowModalDelete] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
  const [isVerificationReportListLoading, setIsVerificationReportListLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const { hasEditPermission, hasDeletePermission } = useRoutePermissions();
  const { canManageProjectWorkflow } = useContext(PermissionContext);

  const navigate = useNavigate();
  const isFirstRender = useRef(true);

  const {
    departmentList,
    verificationReportStatusList,
    scanToolList,
    creatorList,
    fetchSearchInfo,
    verifyReportResultList,
  } = useSearchInfo();

  const allowScanToolKeys = useMemo(
    () => (scanToolList ?? []).map((t) => t.value.toLowerCase()),
    [scanToolList]
  );

  const verificationToolList = [
    {
      key: SCAN_TOOL_OPTIONS.clarity.key,
      value: SCAN_TOOL_OPTIONS.clarity.value,
      label: SCAN_TOOL_OPTIONS.clarity.name,
    },
    {
      key: SCAN_TOOL_OPTIONS.fossid.key,
      value: SCAN_TOOL_OPTIONS.fossid.value,
      label: SCAN_TOOL_OPTIONS.fossid.name,
    },
    {
      key: SCAN_TOOL_OPTIONS.clarityAIR.key,
      value: SCAN_TOOL_OPTIONS.clarityAIR.value,
      label: SCAN_TOOL_OPTIONS.clarityAIR.name,
    },
  ]

  const filteredVerificationToolList = useMemo(() => {
    return verificationToolList.filter((item) =>
      allowScanToolKeys.includes(item.key)
    );
  }, [allowScanToolKeys, verificationToolList])

  const translatedVerificationReportStatusList = useMemo(() => {
    if (!verificationReportStatusList) return [];

    return verificationReportStatusList.map((item) => {
      const value = item.value;

      return {
        value,
        label: t(
          `project.verification-report.verification-report-status-${item.label?.toLowerCase()}`
        ),
      };
    });
  }, [verificationReportStatusList, t]);

  const searchFields = [
    {
      name: "verificationReportId",
      label: t("project.verification-report.verification-report-id"),
      placeholder: t("project.verification-report.verification-report-id-placeholder"),
    },
    {
      name: "verificationReportName",
      label: t("project.verification-report.verification-report-name"),
      placeholder: t("project.verification-report.verification-report-name-placeholder"),
    },
    {
      name: "projectId",
      label: t("project.verification-report.projectId"),
      placeholder: t("project.verification-report.projectId-placeholder"),
    },
    {
      name: "verificationReportStatusCd",
      label: t("project.verification-report.verification-report-status"),
      type: "select",
      placeholder: t("project.verification-report.verification-report-status"),
      keepRawLabel: true,
      options: translatedVerificationReportStatusList,
    },
    {
      name: "verificationToolCd",
      label: t("project.verification-report.verification-tool"),
      placeholder: t("project.verification-report.verification-tool"),
      type: "select",
      keepRawLabel: true,
      options: filteredVerificationToolList,
    },
    {
      name: "createrId",
      label: t("project.verification-report.creator"),
      type: "select",
      placeholder: t("project.verification-report.creator"),
      keepRawLabel: true,
      options: creatorList,
      displayFormatLabelWithID: true,
    },
    {
      name: "createdAtFrom",
      label: t("common.create-at-from"),
      placeholder: t("common.create-at-from"),
      type: "date",
    },
    {
      name: "createdAtTo",
      label: t("common.create-at-to"),
      placeholder: t("common.create-at-to"),
      type: "date",
    },
    // {
    //   name: "createrDeptCd",
    //   label: t("project.verification-report.creator-department"),
    //   type: "select",
    //   placeholder: t("project.verification-report.creator-department"),
    //   keepRawLabel: true,
    //   options: departmentList.map((item) => ({
    //     label: translateDepartment(t, item.value),
    //     value: item.value,
    //   })),
    // },
  ];
  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: searchFields,
    quickSearchField: "search",
    setSearchCriteria: setSearchFilters,
  });

  useEffect(() => {
    fetchData();
  }, [showAdvancedSearch]);


  const columns = [
    "verificationReportId",
    "verificationReportName",
    "verificationReportStatusCdName",
    "verificationReportVersion",
    // "createrDeptCdName",
    "createrName",
    "createdAt",
  ];

  const columnConfig = {
    verificationReportId: {
      headerRender: () => t("project.verification-report.verification-report-id"),
      style: {
        width: "180px",
      }
    },
    verificationReportName: {
      headerRender: () => t("project.verification-report.verification-report-name"),
    },
    verificationReportStatusCdName: {
      headerRender: () => t("project.verification-report.verification-report-status"),
      customRender: (value, row) => renderStatusCell(value),
    },
    verificationReportVersion: {
      headerRender: () => t("project.verification-report.verification-version"),
    },
    // createrDeptCdName: {
    //   headerRender: () => t("project.verification-report.creator-department"),
    //   customRender: (value, row) => {
    //     return <div>{translateDepartment(t, row.createrDeptCdName) || "-"}</div>;
    //   },
    // },
    createrName: {
      headerRender: () => t("project.verification-report.creator"),
      customRender: (value, row) => {
        return <UserNameWithId name={row.createrName} id={row.createrId} />;
      },
    },
    createdAt: {
      headerRender: () => t("common.create-at"),
      customRender: (value) => formatDateTime(value),
    },
  };

  // Status color logic (match ProjectList)
  const renderStatusCell = (status) => {
    const STATUS = {
      "DISTRIBUTION_COMPLETED": "Distribution Completed",
      "WRITING": "Writing...",
      "REVIEWING": "Reviewing...",
    }

    const statusStyles = {
      [STATUS.DISTRIBUTION_COMPLETED]: colors.textSuccess,
      [STATUS.WRITING]: colors.textStatusAnalysis,
      [STATUS.REVIEWING]: colors.textStatusWritingReview,
    };
    const statusColorDot = {
      [STATUS.DISTRIBUTION_COMPLETED]: colors.bgStatusSuccess,
      [STATUS.WRITING]: colors.bgStatusAnalysis,
      [STATUS.REVIEWING]: colors.bgStatusWritingReview,
    };
    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-semibold ${statusStyles[status] || colors.badgeVariantDefault}`}
      >
        <div className={`mr-2 h-2 w-2 rounded-full ${statusColorDot[status]}`}></div>
        {t(`project.verification-report.verification-report-status-${status?.toLowerCase() || ""}`, status)}
      </span>
    );
  };

  // Controls
  const handleDelete = () => setShowModalDelete(true);
  const handleAdd = () => {
    navigate("/verification-report/new");
  };
  const canManageVerificationReport = useCallback(
    (row) => canManageProjectWorkflow(row?.projectManagerId),
    [canManageProjectWorkflow],
  );

  const handleCheckboxChange = (newSelectedRows) => setSelectedRows([...newSelectedRows]);
  const onRowClick = (rowData) => navigate(`./${rowData.verificationReportSeq}`);
  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  // Search handler
  const searchHandler = useCallback((inputData) => {
    setSearchFilters(prev => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  const handleSort = (columnKey, direction) => {
    setSortColumn(columnKey);
    setSortDirection(direction);
  };

  const deleteVerificationReports = useMutationData(
    queryKeys.deleteVerificationReports,
    { method: "DELETE" },
    {
      onSuccess: async () => {
        await deleteDataInIndexedDb(STORES.VERIFICATION_REPORTS, selectedRows);
        useSuccessToast(TOAST.success, t("project.verification-report.verification-report-delete-success"));
        refreshPaginatedListAfterDelete({
          currentPage,
          setCurrentPage,
          itemsPerPage,
          totalData,
          deletedCount: selectedRows.length,
          fetchData,
        });
        setSelectedRows([]);
      },
      onError: (error) => {
        console.error("Error during delete data:", error);
        toast.error(t("project.verification-report.verification-report-delete-error"), { autoClose: 1000 });
      },
    },
  );

  const getListData = async (bodyRequest = {}) => {
    setIsVerificationReportListLoading(true);
    try {
      const {
        pageSize = itemsPerPage,
        startRow = 0,
        sortKey = sortColumn || "createdAt",
        sortOrder = sortDirection || "desc",
        ...filters
      } = bodyRequest;
      const response = await getIndexDBData(STORES.VERIFICATION_REPORTS, {
        pageSize,
        startRow,
        filters,
        sortKey,
        sortOrder,
      });

      if (response && typeof response !== "string") {
        setVerificationReportList(response.object || []);
        const totalCount = response.objectCount || 0;
        setTotalData(totalCount);
        setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
      }
    } catch (error) {
      console.error("Error fetching verification reports from IndexedDB:", error);
    } finally {
      setIsVerificationReportListLoading(false);
    }
  };

  // --- RESTORED FETCH LOGIC ---
  useEffect(() => {
    fetchSearchInfo && fetchSearchInfo();
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchData();
    }
  }, [searchFilters, itemsPerPage, sortColumn, sortDirection]);

  const fetchData = () => {
    const effectiveCriteria = resolveSearchCriteria(searchFilters, {
      quickSearchField: "search",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      let value = effectiveCriteria[key];
      if (typeof value === "string") {
        value = value.trim();
      }
      if (value !== "" && value !== undefined) {
        acc[key] = formatDateForRequest(value);
      }
      return acc;
    }, {});
    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);
    bodyRequest["sortKey"] = sortColumn || "createdAt";
    bodyRequest["sortOrder"] = sortDirection || "desc";
    getListData(bodyRequest);
  };

  // Auto-refresh data when sync completes
  useDataSyncRefresh(fetchData);

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteVerificationReports.mutateAsync(selectedRows);
      setSelectedRows([]);
      setShowModalDelete(false);
    } catch (error) {
      console.error("Error deleting verification reports:", error);
      // Optionally show a toast here
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="verification-report-list-page">
      {/* Header */}
      <div className={`${colors.textPlaceholder} py-1`} data-testid="verification-report-list-header">
        <div className="flex items-center justify-between">
          <h1 className={`text-[28px] font-bold ${colors.textPrimary} uppercase`} data-testid="verification-report-list-title">{t("project.verification-report.verification-report-list")}</h1>
        </div>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 ${colors.bgSecondary}`} data-testid="verification-report-list-controls">
        <TableHeaderControls
          controlsTestId="verification-report-list-table-header-controls"
          searchInputTestId="verification-report-list-search-input"
          addButtonTestId="verification-report-list-add-button"
          deleteButtonTestId="verification-report-list-delete-button"
          advancedSearchButtonTestId="verification-report-list-advanced-search-button"
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          onDeleteProject={handleDelete}
          onAdd={handleAdd}
          searchField="search"
          searchValue={searchFilters.search ?? ""}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          placeholderSearch={t("common.search-placeholder")}
          titleAddButton={t("project.verification-report.add-verif-report")}
          titleDeleteButton={t("project.verification-report.delete-verif-report")}
          isDisplayDeleteButton={hasDeletePermission}
          isDisplayAddButton={hasEditPermission}
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div className="mb-4" data-testid="verification-report-list-advanced-search">
          <AdvancedSearch
            data-testid="verification-report-list-advanced-search-panel"
            title={t("project.advancedSearch")}
            searchFields={searchFields}
            searchHandler={searchHandler}
            withoutSearch={true}
          />
        </div>
      )}

      {/* Table */}
      <div className="flex-grow min-h-0" data-testid="verification-report-list-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={verificationReportList}
          columns={columns}
          onRowClick={onRowClick}
          loading={isVerificationReportListLoading}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          selectedRows={selectedRows}
          onCheckboxChange={handleCheckboxChange}
          disabledRow={[]}
          hasCheckbox={hasDeletePermission}
          isCheckboxDisabled={(row) => !canManageVerificationReport(row)}
          hasRadioButton={false}
          rowKey="verificationReportSeq"
          shouldShowGoToPage={true}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          showItemsPerPageSelector={true}
          onSort={handleSort}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          sortable={true}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        data-testid="verification-report-list-delete-modal"
        isOpen={showModalDelete}
        onClose={() => setShowModalDelete(false)}
        onConfirm={handleConfirmDelete}
        title={t("project.verification-report.confirm-delete-title")}
        description={t("project.verification-report.confirm-delete-body", { count: selectedRows.length })}
        cancelText={t("common.cancel-button")}
        confirmText={t("common.delete-button")}
        loading={deleteLoading}
      />
    </div>
  );
};

export default VerificationReportList;
